import React, { useState, useEffect, useRef } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Grid, TextField, Button, Divider,
  Avatar, Chip, Alert, CircularProgress, Skeleton, IconButton, Tooltip,
} from "@mui/material";
import SaveIcon        from "@mui/icons-material/Save";
import LockResetIcon   from "@mui/icons-material/LockReset";
import EditIcon        from "@mui/icons-material/Edit";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { isAutheticated } from "../auth/helper";
import { getProfile, updateAdminProfile, uploadAdminAvatar } from "./helper/adminapicall";
import { API } from "../backend";

const Profile = () => {
  const auth    = isAutheticated();
  const adminId = auth?.admin?._id;
  const isAdmin = auth?.admin?.role === 1;

  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [avatarTs, setAvatarTs] = useState(Date.now());

  // Info edit form
  const [infoForm,   setInfoForm]   = useState({ name: "", lastname: "", phone: "" });
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoAlert,  setInfoAlert]  = useState(null);

  // Password form
  const [pwForm,   setPwForm]   = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwAlert,  setPwAlert]  = useState(null);

  // Avatar
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarAlert,   setAvatarAlert]   = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!adminId) return;
    getProfile(adminId, null).then((data) => {
      // getProfile returns the object directly (not wrapped in { admin: ... })
      if (data?._id) {
        setProfile(data);
        setInfoForm({
          name:     data.name     || "",
          lastname: data.lastname || "",
          phone:    data.phone    || "",
        });
      }
      setLoading(false);
    });
  }, [adminId]);

  // ── Info save ────────────────────────────────────────────────────────────────
  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    if (!infoForm.name.trim()) {
      setInfoAlert({ severity: "error", msg: "First name is required." });
      return;
    }
    setInfoSaving(true);
    setInfoAlert(null);
    const data = await updateAdminProfile(adminId, null, {
      name:     infoForm.name.trim(),
      lastname: infoForm.lastname.trim(),
      phone:    infoForm.phone.trim(),
    });
    setInfoSaving(false);
    if (data?.error) {
      setInfoAlert({ severity: "error", msg: data.error });
    } else {
      setInfoAlert({ severity: "success", msg: "Profile updated." });
      setProfile(p => ({ ...p, name: infoForm.name.trim(), lastname: infoForm.lastname.trim(), phone: infoForm.phone.trim() }));
    }
  };

  // ── Password save ────────────────────────────────────────────────────────────
  const handlePwSubmit = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwAlert({ severity: "error", msg: "New passwords do not match." });
      return;
    }
    if (pwForm.newPassword.length < 6) {
      setPwAlert({ severity: "error", msg: "New password must be at least 6 characters." });
      return;
    }
    setPwSaving(true);
    setPwAlert(null);
    const data = await updateAdminProfile(adminId, null, {
      currentPassword: pwForm.currentPassword,
      newPassword:     pwForm.newPassword,
    });
    setPwSaving(false);
    if (data?.error) {
      setPwAlert({ severity: "error", msg: data.error });
    } else {
      setPwAlert({ severity: "success", msg: "Password updated successfully." });
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    }
  };

  // ── Avatar ───────────────────────────────────────────────────────────────────
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarLoading(true);
    setAvatarAlert(null);
    const form = new FormData();
    form.append("avatar", file);
    const data = await uploadAdminAvatar(adminId, form);
    setAvatarLoading(false);
    if (data?.error) {
      setAvatarAlert({ severity: "error", msg: data.error });
    } else {
      setAvatarAlert({ severity: "success", msg: "Avatar updated." });
      setAvatarTs(Date.now());
    }
    fileRef.current.value = "";
  };

  const avatarSrc = `${API}/uploads/admins/${adminId}.jpg?t=${avatarTs}`;
  const displayName = profile
    ? `${profile.name || ""} ${profile.lastname || ""}`.trim() || "—"
    : null;

  return (
    <Base>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">My Profile</Typography>
        <Typography variant="body2" color="text.secondary">
          Account details and password management
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* ── Left: avatar + status info ──────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>

            {/* Avatar */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
              <Box sx={{ position: "relative" }}>
                <Avatar
                  src={avatarSrc}
                  sx={{ width: 80, height: 80, bgcolor: "primary.main", fontSize: "1.8rem", fontWeight: 700 }}
                >
                  {(profile?.name || auth?.admin?.name || "?")[0]?.toUpperCase()}
                </Avatar>
                <Tooltip title="Change photo">
                  <IconButton
                    size="small"
                    onClick={() => fileRef.current.click()}
                    disabled={avatarLoading}
                    sx={{
                      position: "absolute", bottom: -4, right: -4,
                      bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
                      width: 28, height: 28,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    {avatarLoading
                      ? <CircularProgress size={14} />
                      : <PhotoCameraIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Tooltip>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                  style={{ display: "none" }} onChange={handleAvatarChange} />
              </Box>
              {loading
                ? <Skeleton width={120} height={20} sx={{ mt: 1.5 }} />
                : <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5 }}>{displayName}</Typography>
              }
              {avatarAlert && (
                <Alert severity={avatarAlert.severity} sx={{ mt: 1.5, py: 0, fontSize: "0.78rem" }}>
                  {avatarAlert.msg}
                </Alert>
              )}
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.4 }}>Email</Typography>
                {loading ? <Skeleton width={160} height={22} /> : <Typography variant="body2">{profile?.email || "—"}</Typography>}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Role</Typography>
                {loading
                  ? <Skeleton width={90} height={24} />
                  : <Chip
                      label={profile?.role === 1 ? "Super Admin" : "Sub Admin"}
                      size="small"
                      color={profile?.role === 1 ? "primary" : "default"}
                      sx={{ fontWeight: 600 }}
                    />
                }
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Status</Typography>
                {loading
                  ? <Skeleton width={70} height={24} />
                  : <Chip
                      label={profile?.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={profile?.isActive ? "success" : "error"}
                    />
                }
              </Box>
              {profile?.createdAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.4 }}>Member Since</Typography>
                  <Typography variant="body2">
                    {new Date(profile.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </Typography>
                </Box>
              )}
              {!isAdmin && profile?.allowedPages?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Page Access</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                    {profile.allowedPages.map((p) => (
                      <Chip key={p} label={p} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* ── Right: edit info + change password ──────────────────────────── */}
        <Grid item xs={12} md={8}>

          {/* Edit name / phone */}
          <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <EditIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="h6" fontSize="1rem" fontWeight={600}>Account Info</Typography>
            </Box>

            <Box component="form" onSubmit={handleInfoSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="First Name *"
                    value={infoForm.name}
                    onChange={(e) => setInfoForm(f => ({ ...f, name: e.target.value }))}
                    disabled={infoSaving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Last Name"
                    value={infoForm.lastname}
                    onChange={(e) => setInfoForm(f => ({ ...f, lastname: e.target.value }))}
                    disabled={infoSaving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Phone"
                    value={infoForm.phone}
                    onChange={(e) => setInfoForm(f => ({ ...f, phone: e.target.value }))}
                    disabled={infoSaving}
                    placeholder="e.g. +91 98765 43210"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Email" value={profile?.email || ""} disabled
                    helperText="Email cannot be changed here" />
                </Grid>
              </Grid>

              {infoAlert && <Alert severity={infoAlert.severity} sx={{ mt: 2 }}>{infoAlert.msg}</Alert>}

              <Box sx={{ mt: 2.5 }}>
                <Button type="submit" variant="contained" size="small"
                  startIcon={infoSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  disabled={infoSaving}>
                  {infoSaving ? "Saving…" : "Save Changes"}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Change password */}
          <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <LockResetIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="h6" fontSize="1rem" fontWeight={600}>Change Password</Typography>
            </Box>

            {!isAdmin && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Password changes are only available to Super Admins. Contact your administrator.
              </Alert>
            )}

            <Box component="form" onSubmit={handlePwSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Current Password"
                    name="currentPassword" type="password"
                    value={pwForm.currentPassword}
                    onChange={(e) => { setPwForm(f => ({ ...f, currentPassword: e.target.value })); setPwAlert(null); }}
                    disabled={!isAdmin || pwSaving}
                    autoComplete="current-password"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="New Password"
                    name="newPassword" type="password"
                    value={pwForm.newPassword}
                    onChange={(e) => { setPwForm(f => ({ ...f, newPassword: e.target.value })); setPwAlert(null); }}
                    disabled={!isAdmin || pwSaving}
                    autoComplete="new-password"
                    helperText="Minimum 6 characters"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Confirm New Password"
                    name="confirmPassword" type="password"
                    value={pwForm.confirmPassword}
                    onChange={(e) => { setPwForm(f => ({ ...f, confirmPassword: e.target.value })); setPwAlert(null); }}
                    disabled={!isAdmin || pwSaving}
                    autoComplete="new-password"
                  />
                </Grid>
              </Grid>

              {pwAlert && <Alert severity={pwAlert.severity} sx={{ mt: 2 }}>{pwAlert.msg}</Alert>}

              <Box sx={{ mt: 2.5 }}>
                <Button type="submit" variant="contained" size="small"
                  startIcon={pwSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  disabled={!isAdmin || pwSaving || !pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword}>
                  {pwSaving ? "Saving…" : "Update Password"}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Base>
  );
};

export default Profile;
